import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartUpdateEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';
import { formatMoney } from '@theme/money-formatting';
import { bill } from '@theme/uc-cart-core';
import { cartService } from '@theme/uc-cart-service';

/** Shared drawer; its dialog stays mounted while hydration replaces only cart presentation. */
class CartDrawerComponent extends DialogComponent {
  #abort;
  #unsubscribe;
  #config;
  #sheet;
  #trigger;
  #sheetTrigger;
  #noteDirty = false;
  #historyOwned = false;
  #closing = false;
  #toastTimer;
  #disabled = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    if (history.state?.ucCart && !this.#historyOwned) { const { ucCart, ...rest } = history.state; history.replaceState(rest, ''); }
    const { signal } = this.#abort;
    this.#config = JSON.parse(this.querySelector('[data-uc-config]').textContent);
    for (const [key, value] of Object.entries(this.#config.strings)) this.#config.strings[key] = new DOMParser().parseFromString(value, 'text/html').body.textContent;
    cartService.configure(this.#config);
    this.#unsubscribe = cartService.subscribe((state) => this.#render(state));
    document.addEventListener('click', this.#openTrigger, { signal });
    document.addEventListener('uc-cart:add-confirmed', () => {
      if (this.hasAttribute('auto-open') && !this.refs.dialog.open) this.#reveal();
    }, { signal });
    document.addEventListener('uc-cart:busy', this.#busy, { signal });
    document.addEventListener('uc-cart:error', this.#error, { signal });
    document.addEventListener('cart:update', this.#externalUpdate, { signal });
    document.addEventListener('discount:update', this.#externalUpdate, { signal });
    this.addEventListener('click', this.#click, { signal });
    this.addEventListener('submit', this.#submit, { signal });
    this.addEventListener('input', (event) => { if (event.target.matches('[name="note"]')) this.#noteDirty = true; }, { signal });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.#sheet) { event.preventDefault(); event.stopImmediatePropagation(); this.#closeSheet(); }
    }, { capture: true, signal });
    this.refs.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); }, { signal });
    this.addEventListener(DialogOpenEvent.eventName, () => {
      if (matchMedia('(max-width: 749px)').matches && !history.state?.ucCart) {
        history.pushState({ ...history.state, ucCart: true }, ''); this.#historyOwned = true;
      }
    }, { signal });
    this.addEventListener(DialogCloseEvent.eventName, () => {
      this.#closeSheet(false);
      if (this.#historyOwned && history.state?.ucCart) { this.#historyOwned = false; history.back(); }
      const target = this.#trigger?.isConnected && this.#trigger.getClientRects().length ? this.#trigger : document.querySelector('[data-uc-cart-trigger]');
      target?.focus({ preventScroll: true });
    }, { signal });
    window.addEventListener('popstate', () => { if (this.#historyOwned && !history.state?.ucCart) { this.#historyOwned = false; this.close(); } }, { signal });
    window.visualViewport?.addEventListener('resize', this.#viewport, { signal });
    window.visualViewport?.addEventListener('scroll', this.#viewport, { signal });
    // No persisted trial state: always fetch server truth, including on a theme-editor remount.
    this.#refresh();
  }
  disconnectedCallback() {
    this.#abort?.abort(); this.#unsubscribe?.(); clearTimeout(this.#toastTimer);
    if (this.refs.dialog?.open) this.closeDialog();
    super.disconnectedCallback();
  }
  async #refresh() {
    const recovering = !this.querySelector('[data-uc-retry]').hidden;
    try { await cartService.refresh(); this.querySelector('[data-uc-retry]').hidden = true; if (recovering) this.#status(''); }
    catch (_) { this.#error({ detail: { kind: 'refresh' } }); }
  }
  #openTrigger = (event) => {
    const trigger = event.target instanceof Element && event.target.closest('[data-uc-cart-trigger]');
    if (!trigger || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault(); this.#trigger = trigger; this.open();
  };
  async #reveal() {
    this.#trigger = document.activeElement;
    const quickAdd = document.getElementById('quick-add-dialog');
    if (quickAdd?.querySelector('dialog[open]')) await quickAdd.closeDialog();
    for (const menu of document.querySelectorAll('header-drawer')) {
      if (menu.querySelector('details[open]')) {
        menu.close?.();
        await new Promise((resolve) => {
          const details = menu.querySelector('details[open]');
          if (!details) return resolve();
          details.addEventListener('toggle', resolve, { once: true, signal: this.#abort.signal });
        });
      }
    }
    if (this.isConnected) this.showDialog();
  }
  open() {
    if (!this.refs.dialog.open) this.#reveal();
    this.#refresh();
  }
  close() {
    if (this.#closing) return;
    this.#closing = true;
    this.closeDialog().finally(() => { this.#closing = false; });
  }
  #externalUpdate = (event) => {
    const source = event.detail?.data?.source;
    // These producers already use the queue and publish to this subscriber before emitting their native event.
    if (['product-form-component', 'cart-items-component', 'quick-order-quantity', 'quick-order-remove-all', 'uc-quick-add', 'uc-cart'].includes(source)) return;
    if (event.detail?.resource === cartService.cart || event.detail?.resource?.sections) return;
    this.#refresh();
  };
  #busy = (event) => {
    const busy = event.detail.busy;
    this.setAttribute('aria-busy', String(busy));
    if (busy) {
      for (const button of this.querySelectorAll('[data-uc-quantity], [data-uc-add], [data-uc-combo-open], [data-uc-combo-form] button, [data-uc-code], [data-uc-remove], [data-uc-checkout], [data-uc-coupon-form] button, [data-uc-note-form] button')) {
        if (!button.disabled) { this.#disabled.add(button); button.disabled = true; }
      }
    } else {
      for (const button of this.#disabled) button.disabled = false;
      this.#disabled.clear();
    }
  };
  #error = (event) => {
    const kind = event.detail.kind;
    this.#status(this.#config.strings[kind === 'refresh' ? (event.detail.added ? 'added_refresh' : 'refresh_error') : kind === 'busy' ? 'busy' : 'offer_error']);
    if (kind === 'refresh') this.querySelector('[data-uc-retry]').hidden = false;
  };
  #status(message, toast = false) {
    clearTimeout(this.#toastTimer);
    this.querySelector('[data-uc-status]').textContent = message || '';
    this.querySelector('.uc-cart__feedback').classList.toggle('is-toast', toast && Boolean(message));
    if (toast) this.#toastTimer = setTimeout(() => this.#status(''), 5000);
  }
  #openSheet(name, trigger) {
    this.#sheet = name; this.#sheetTrigger = trigger;
    this.querySelector('[data-uc-main]').inert = true;
    this.querySelector('.uc-cart__sheet-backdrop').hidden = false;
    const panel = this.querySelector(`[data-uc-panel="${name}"]`);
    panel.hidden = false;
    this.refs.dialog.setAttribute('aria-labelledby', panel.getAttribute('aria-labelledby'));
    panel.insertBefore(this.querySelector('.uc-cart__feedback'), panel.querySelector('form')?.closest('.uc-cart__sheet-body') || panel.querySelector('form'));
    panel.querySelector('input, textarea, button')?.focus({ preventScroll: true });
    this.#viewport();
  }
  #closeSheet(focus = true) {
    this.#sheet = null;
    this.querySelector('[data-uc-main]').inert = false;
    this.querySelector('.uc-cart__sheet-backdrop').hidden = true;
    this.querySelector('.uc-cart__shell').append(this.querySelector('.uc-cart__feedback'));
    this.refs.dialog.setAttribute('aria-labelledby', this.querySelector('h2').id);
    for (const panel of this.querySelectorAll('[data-uc-panel]')) panel.hidden = true;
    this.#viewport();
    if (focus) {
      if (this.#sheetTrigger?.isConnected) this.#sheetTrigger.focus({ preventScroll: true });
      else (this.querySelector(`[data-focus-key="${CSS.escape(this.#sheetTrigger?.dataset.focusKey || '')}"]`) || this.querySelector('[data-uc-close]'))?.focus({ preventScroll: true });
    }
  }
  #viewport = () => {
    const viewport = window.visualViewport;
    const shell = this.querySelector('.uc-cart__shell');
    // Keep the cart geometry stable. Only lift/constrain the sheet above the keyboard.
    if (!this.#sheet || !viewport) {
      shell.style.removeProperty('--uc-cart-sheet-bottom');
      shell.style.removeProperty('--uc-cart-sheet-height');
      return;
    }
    const bottom = Math.max(0, this.refs.dialog.getBoundingClientRect().bottom - viewport.offsetTop - viewport.height);
    shell.style.setProperty('--uc-cart-sheet-bottom', `${bottom}px`);
    shell.style.setProperty('--uc-cart-sheet-height', `${viewport.height}px`);
  };
  #click = async (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button');
    if (!button || button.disabled) return;
    if (button.hasAttribute('data-uc-close')) return this.close();
    if (button.hasAttribute('data-uc-back')) return this.#closeSheet();
    if (button.dataset.ucSheet) return this.#openSheet(button.dataset.ucSheet, button);
    if (button.hasAttribute('data-uc-retry')) return this.#refresh();
    if (cartService.busy) return;
    if (button.hasAttribute('data-uc-combo-open')) {
      const template = button.closest('.uc-cart__combo-card')?.querySelector('template[data-uc-combo-choices]');
      if (!(template instanceof HTMLTemplateElement)) return;
      this.querySelector('[data-uc-combo-content]').replaceChildren(template.content.cloneNode(true));
      this.#openSheet('combo', button);
      return;
    }
    try {
      if (button.dataset.ucQuantity !== undefined) {
        const row = button.closest('[data-key]');
        await this.#write('change', { id: row.dataset.key, quantity: Number(button.dataset.ucQuantity) });
      } else if (button.dataset.ucAdd) {
        button.textContent = this.#config.strings.adding;
        const result = await this.#write('add', { id: Number(button.dataset.ucAdd), quantity: 1 });
        if (!result.refresh_failed) this.#status(this.#config.strings.added, true);
      } else if (button.dataset.ucCode) await this.#applyCode(button.dataset.ucCode);
      else if (button.dataset.ucRemove !== undefined) {
        const codes = (cartService.cart?.discount_codes || []).filter((code) => code.code.toLowerCase() !== button.dataset.ucRemove.toLowerCase()).map((code) => code.code);
        await this.#write('update', { discount: codes.join(',') });
        this.#status(this.#config.strings.removed, true);
      }
    } catch (error) { this.#status(error.message || this.#config.strings.error); }
  };
  #submit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.hasAttribute('data-uc-checkout-form')) {
      if (cartService.busy || !form.reportValidity()) { event.preventDefault(); event.stopImmediatePropagation(); }
      return;
    }
    event.preventDefault();
    if (cartService.busy) return;
    try {
      if (form.hasAttribute('data-uc-combo-form')) {
        if (!form.reportValidity()) return;
        const selected = form.querySelector('input[name="id"]:checked:not(:disabled)');
        if (!(selected instanceof HTMLInputElement)) return;
        const id = Number(selected.value);
        const quantity = Number(selected.dataset.quantity || 1);
        if (!Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(quantity) || quantity <= 0) return;
        const result = await this.#write('add', { id, quantity });
        if (this.#sheet === 'combo' && this.querySelector('[data-uc-combo-form]') === form) this.#closeSheet();
        if (!result.refresh_failed) this.#status(this.#config.strings.added, true);
      }
      if (form.hasAttribute('data-uc-coupon-form')) await this.#applyCode(new FormData(form).get('discount').trim());
      if (form.hasAttribute('data-uc-note-form')) {
        await this.#write('update', { note: new FormData(form).get('note') });
        this.#noteDirty = false; this.#closeSheet(); this.#status(this.#config.strings.saved, true);
      }
    } catch (error) { this.#status(error.message || this.#config.strings.error); }
  };
  async #applyCode(code) {
    if (!code) return;
    const result = await this.#write('update', { discount: code });
    if (result.refresh_failed) return;
    const active = result.discount_codes?.some((entry) => entry.applicable && entry.code.toLowerCase() === code.toLowerCase());
    if (active) {
      this.#closeSheet(); this.#status(this.#config.strings.applied, true);
      const trigger = this.querySelector('[data-uc-sheet="coupons"]');
      trigger?.classList.add('is-just-applied');
      trigger?.addEventListener('animationend', () => trigger.classList.remove('is-just-applied'), { once: true, signal: this.#abort.signal });
    }
    else this.#status(this.#config.strings.inapplicable);
  }
  async #write(operation, body) {
    const result = await cartService.write(operation, body);
    const cart = result.cart || result;
    if (!result.refresh_failed) document.dispatchEvent(new CartUpdateEvent(cart, this.dataset.sectionId, {
      source: 'uc-cart', itemCount: cart.item_count, sections: result.sections,
    }));
    return result;
  }
  async #render({ cart, sections, added }) {
    if (!this.isConnected) return;
    const active = document.activeElement;
    const focusKey = active instanceof HTMLElement && active.dataset.focusKey;
    const terms = this.querySelector('[data-uc-terms]')?.checked;
    const scroll = this.querySelector('.uc-cart__body')?.scrollTop;
    const itemScroll = this.querySelector('.uc-cart__items')?.scrollTop;
    const html = sections[this.dataset.sectionId];
    if (!html) throw new Error('Missing cart section');
    await morphSection(this.dataset.sectionId, html, 'hydration', { injectStylesheet: true });
    if (!this.isConnected) return;
    if (this.querySelector('[data-uc-terms]')) this.querySelector('[data-uc-terms]').checked = terms || false;
    if (!this.#noteDirty) this.querySelector('[name="note"]').value = cart.note || '';
    if (this.querySelector('.uc-cart__body')) this.querySelector('.uc-cart__body').scrollTop = scroll || 0;
    if (this.querySelector('.uc-cart__items')) this.querySelector('.uc-cart__items').scrollTop = itemScroll || 0;
    this.#renderDiscounts(cart);
    this.#busy({ detail: { busy: cartService.busy } });
    if (focusKey && this.refs.dialog.open && !this.#sheet) {
      (this.querySelector(`[data-focus-key="${CSS.escape(focusKey)}"]`) || this.querySelector('[data-uc-close]'))?.focus({ preventScroll: true });
    }

  }
  #renderDiscounts(cart) {
    const focusedRemoval = document.activeElement instanceof HTMLElement ? document.activeElement.dataset.ucRemove : undefined;
    const strings = this.#config.strings;
    // Shopify Ajax/Liquid monetary values use hundredths, including zero-decimal currencies.
    const money = (amount) => {
      if (cart.currency !== this.#config.shopCurrency) return new Intl.NumberFormat(document.documentElement.lang, { style: 'currency', currency: cart.currency }).format(amount / 100);
      const formatted = formatMoney(amount, this.#config.moneyFormat.replace(/{{\s*currency\s*}}/g, cart.currency), 'USD');
      return new DOMParser().parseFromString(formatted, 'text/html').body.textContent;
    };
    const rows = this.querySelector('[data-uc-discounts]');
    if (rows) {
      rows.replaceChildren();
      for (const row of bill(cart).rows) {
        const element = document.createElement('div'); element.className = 'uc-cart__bill-row';
        const name = document.createElement('span'); name.textContent = (row.title || strings.discounts) + (row.percent === null ? '' : ` (${row.percent}%)`);
        const amount = document.createElement('span'); amount.textContent = `−${money(row.amount)}`;
        element.append(name, amount); rows.append(element);
      }
    }
    const apps = new Map();
    for (const item of cart.items || []) for (const allocation of item.line_level_discount_allocations || []) {
      if (allocation.amount > 0) apps.set(`${allocation.discount_application.type}:${allocation.discount_application.title}`, allocation.discount_application);
    }
    for (const app of cart.cart_level_discount_applications || []) if (app.total_allocated_amount > 0) apps.set(`${app.type}:${app.title}`, app);
    // Include applicable shipping codes even when they do not reduce merchandise totals.
    for (const code of cart.discount_codes || []) if (code.applicable && ![...apps.values()].some((app) => (app.code || app.title).toLowerCase() === code.code.toLowerCase())) apps.set(`discount_code:${code.code}`, { type: 'discount_code', title: code.code });
    const container = this.querySelector('[data-uc-active]'); container.replaceChildren();
    for (const app of apps.values()) {
      const row = document.createElement('div'); row.className = 'uc-cart__offer uc-cart__offer--active';
      const tick = document.createElement('span'); tick.className = 'uc-cart__applied-tick'; tick.setAttribute('aria-hidden', 'true'); tick.textContent = '✓';
      const copy = document.createElement('div'); copy.className = 'uc-cart__applied-copy';
      const state = document.createElement('small'); state.textContent = app.type === 'discount_code' ? strings.coupon_applied : app.type === 'automatic' ? strings.automatic_applied : strings.applied;
      const label = document.createElement('strong'); label.textContent = (app.title || app.code) + (app.value_type === 'percentage' ? ` · ${Number(app.value)}%` : '');
      copy.append(state, label); row.append(tick, copy);
      if (app.type === 'discount_code') {
        const remove = document.createElement('button'); remove.type = 'button'; remove.className = 'button-unstyled'; remove.textContent = strings.remove; remove.dataset.ucRemove = app.code || app.title; row.append(remove);
      }
      container.append(row);
    }
    const codes = [...(cart.discount_codes || []).filter((code) => code.applicable).map((code) => code.code.toLowerCase()), ...[...apps.values()].filter((app) => app.type === 'discount_code').map((app) => (app.code || app.title).toLowerCase())];
    for (const card of this.querySelectorAll('[data-uc-offer]')) card.hidden = codes.includes(card.dataset.ucOffer.toLowerCase());
    const label = this.querySelector('[data-uc-offer-label]');
    if (label) label.textContent = apps.size ? [...apps.values()].map((app) => app.title || app.code).join(' + ') : strings.offers;
    const trigger = this.querySelector('[data-uc-sheet="coupons"]');
    trigger?.classList.toggle('uc-cart__coupon--applied', apps.size > 0);
    if (trigger) {
      trigger.querySelector('[data-uc-offer-badge]').textContent = apps.size ? '✓' : '%';
      trigger.querySelector('[data-uc-offer-caption]').textContent = apps.size ? strings.applied_hint : strings.apply_hint;
      trigger.querySelector('[data-uc-offer-action]').textContent = apps.size ? strings.view : strings.apply;
    }
    const heading = this.querySelector('[data-uc-offers-heading]');
    if (heading) heading.hidden = !this.querySelector('[data-uc-offer]:not([hidden])');
    if (focusedRemoval !== undefined && this.#sheet === 'coupons') {
      (this.querySelector(`[data-uc-remove="${CSS.escape(focusedRemoval)}"]`) || this.querySelector('[data-uc-coupon-form] input'))?.focus({ preventScroll: true });
    }
  }
}
if (!customElements.get('cart-drawer-component')) customElements.define('cart-drawer-component', CartDrawerComponent);
